export function toDateToPrettyString(currentDate: Date): string {
	return `${currentDate.toLocaleDateString("de", { dateStyle: "medium" })} um ${currentDate.toLocaleTimeString("de", {
		timeStyle: "short"
	})}`;
}

/**
 * Parses a date-time string in A12 format (yyyy-MM-dd'T'HH:mm:ss) and returns
 * a JavaScript Date object interpreted in Berlin timezone (Europe/Berlin).
 *
 * @param dateTimeString - The date-time string in format "yyyy-MM-dd'T'HH:mm:ss" (e.g., "2026-07-01T14:30:00")
 * @returns A JavaScript Date object in Berlin timezone, or null if parsing fails
 *
 * @example
 * const date = parseDateTimeForA12("2026-07-01T14:30:00");
 * // Returns a Date object representing 14:30:00 in Berlin time on July 1, 2026
 */
export function parseDateTimeForA12(dateTimeString: any): Date | null {
	if (typeof dateTimeString !== "string") {
		return null;
	}

	if (!dateTimeString || typeof dateTimeString !== "string") {
		return null;
	}

	// Parse the A12 format: yyyy-MM-dd'T'HH:mm:ss
	const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
	if (!iso8601Regex.test(dateTimeString)) {
		return null;
	}

	try {
		// Get the offset between UTC and Berlin timezone
		const berlinFormatter = new Intl.DateTimeFormat("de-DE", {
			timeZone: "Europe/Berlin",
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false
		});

		// Parse the input string directly as if it were already in Berlin time
		const [year, month, day, hour, minute, second] = dateTimeString.split(/[-T:]/).map(Number);

		// Create a UTC date from the components, treating the parsed values as local Berlin time
		// We need to find what UTC time corresponds to the given Berlin local time
		const testDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

		// Get what time this UTC date represents in Berlin
		const parts = berlinFormatter.formatToParts(testDate);
		const berlinHour = parseInt(parts.find(p => p.type === "hour")?.value || "0", 10);
		const berlinMinute = parseInt(parts.find(p => p.type === "minute")?.value || "0", 10);
		const berlinSecond = parseInt(parts.find(p => p.type === "second")?.value || "0", 10);

		// Calculate the offset and adjust
		const offsetMs =
			(hour - berlinHour) * 3600000 + (minute - berlinMinute) * 60000 + (second - berlinSecond) * 1000;
		return new Date(testDate.getTime() + offsetMs);
	} catch {
		return null;
	}
}
